import React, { useCallback, useEffect, useRef, useState } from 'react';
import _map from 'lodash/map';
import _join from 'lodash/join';
import { useDispatch, useMappedState } from 'redux-react-hook';
import { selectSearchResult, updateSearchTerm } from 'redux/actions/consultation/answer';
import TextField from 'styles/blocks/textField';
import { useAutoFocus } from 'common/customHooks';
import { SkipSearchButton, ResultsContainer, SearchResultButton, TypeaheadSearchContainer } from 'styles/blocks/typeahead';
import { NextButton } from '../nextButton';

export const getSearchResultDisplayName = (result, searchDisplayFields) => {
  const displayNames = _map(searchDisplayFields, searchDisplayField => result[searchDisplayField]);
  return _join(displayNames, ', ');
};

export function Search({ isInvalid, questionId, questionText, isMounted, onClickNextQuestion }) {
  const mapState = useCallback(state => ({
    searchTerm: state.consultationReducer.searchTerm,
    searchResults: state.consultationReducer.searchResults,
    searchDisplayFields: state.consultationReducer.question.searchDisplayFields,
    searchNotFoundButtonText: state.consultationReducer.question.searchNotFoundButtonText,
    tags: state.consultationReducer.question.tags,
    selectedSearchResult: state.consultationReducer.selectedSearchResult,
  }), []);
  const { searchTerm, searchResults, searchDisplayFields, searchNotFoundButtonText, selectedSearchResult } = useMappedState(mapState);
  const dispatch = useDispatch();
  const [isInputFocussed, setIsInputFocussed] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollRef = useRef(null);
  const isSearchResultSelected = !!(selectedSearchResult && searchTerm === getSearchResultDisplayName(selectedSearchResult, searchDisplayFields));

  useEffect(() => {
    const scrollRefVar = scrollRef.current;
    if (scrollRefVar) {
      scrollRefVar.addEventListener('scroll', () => setIsScrolling(true));
      scrollRefVar.addEventListener('scrollend', () => setIsScrolling(false));
    }

    return () => {
      if (scrollRefVar) {
        scrollRefVar.removeEventListener('scroll', () => {});
        scrollRefVar.removeEventListener('scrollend', () => {});
      }
    };
  }, []);

  const handleUpdateSearchTerm = search => {
    dispatch(updateSearchTerm(search));
    if (search?.length === 0) {
      dispatch(selectSearchResult(null, null));
    }
  };

  const handleSkipSearch = () => {
    dispatch(selectSearchResult(searchResults.default, searchNotFoundButtonText));
    return onClickNextQuestion();
  };

  const handleSelectResult = (result, displayName) => {
    setIsScrolling(false);
    if (!isScrolling) {
      dispatch(selectSearchResult(result, displayName));
      setIsInputFocussed(false);
    }
  };

  const handleNextQuestion = () => {
    if (searchTerm?.length === 0) {
      dispatch(selectSearchResult(null, null));
      return onClickNextQuestion();
    }
    if (searchTerm === searchNotFoundButtonText) {
      return handleSkipSearch();
    }
    if (isSearchResultSelected) {
      const displayName = getSearchResultDisplayName(selectedSearchResult, searchDisplayFields);
      dispatch(selectSearchResult(selectedSearchResult, displayName));
      return onClickNextQuestion();
    }
    if (!isSearchResultSelected) {
      // clear and reset on attempt of bad data
      dispatch(selectSearchResult(null, null));
    }
    return onClickNextQuestion();
  };

  const handleOnBlur = event => {
    if (event.relatedTarget?.getAttribute('data-automation') === 'search-result') {
      return null;
    }
    return setIsInputFocussed(false);
  };

  const handleKeyDown = event => {
    if (event.keyCode === 13) {
      // prevent enter to skip dropdown, for the question - What is the name of your registered GP practice?
      event.preventDefault();
      if (!isSearchResultSelected) {
        dispatch(selectSearchResult(null, null));
        return onClickNextQuestion();
      }
    }
    setIsInputFocussed(true);
    return handleUpdateSearchTerm(event.target.value);
  };

  const inputRef = useRef();
  useAutoFocus(true, isMounted, inputRef);
  return (
    <TextField data-automation="typeahead">
      <TypeaheadSearchContainer>
        <TextField.Input
          type="text"
          data-automation="search-input"
          value={searchTerm}
          onChange={event => handleUpdateSearchTerm(event.target.value)}
          onKeyDown={event => handleKeyDown(event)}
          isInvalid={isInvalid}
          ref={inputRef}
          aria-label={questionText}
          placeholder="Tap here to start typing"
          onFocus={() => setIsInputFocussed(true)}
          onBlur={handleOnBlur}
        />
        <ResultsContainer
          isHidden={searchResults.results.length === 0 || !isInputFocussed}
          data-automation="search-results-container"
          ref={scrollRef}
        >
          {_map(searchResults.results, (result, index) => {
            const displayName = getSearchResultDisplayName(result, searchDisplayFields);
            return (
              <SearchResultButton
                key={`search-result-${index}`}
                type="button"
                onClick={() => handleSelectResult(result, displayName)}
                onTouchEnd={() => handleSelectResult(result, displayName)}
                data-automation="search-result"
              >
                {displayName}
              </SearchResultButton>
            );
          })}
        </ResultsContainer>
      </TypeaheadSearchContainer>
      <SkipSearchButton onClick={handleSkipSearch} data-automation="skip-search-button">
        {searchNotFoundButtonText || 'Not listed'}
      </SkipSearchButton>
      <NextButton onClick={handleNextQuestion} dataAutomation={`nextButton-${questionId}`} />
    </TextField>
  );
}
